import { z } from 'zod';

import type { TFunction } from 'i18next';

import { BOOKING_TYPES, SERVICE_CUSTOM_FIELD_TYPES } from '@/types';

export const SERVICE_NAME_MAX_LENGTH = 120;

export const BOOKING_DURATION_UNIT_MINUTES = 30;

export const BOOKING_TYPE_NONE = 'none' as const;
export const BOOKING_TYPE_PERSONAL = 'personal' as const;
export const BOOKING_TYPE_GROUP = 'group' as const;

export const CUSTOM_FIELD_TYPE_DROPDOWN = 'dropdown' as const;

export const SUBJECT_TYPE_STAFF = 'Staff' as const;
export const SUBJECT_TYPE_PERMISSION_GROUP = 'PermissionGroup' as const;

export const SERVICE_SUBJECT_TYPES = [
  SUBJECT_TYPE_STAFF,
  SUBJECT_TYPE_PERMISSION_GROUP,
] as const;

const isNonNegativeNumber = (value: string) =>
  value.trim() !== '' && Number(value) >= 0;

const createSubjectRowSchema = (t: TFunction) =>
  z.object({
    record_id: z.number().nullable(),
    subject_type: z.enum(SERVICE_SUBJECT_TYPES),
    subject_id: z.string().min(1, { message: t('validation_required') }),
  });

const createCustomFieldRowSchema = (t: TFunction) =>
  z.object({
    record_id: z.number().nullable(),
    label: z
      .string()
      .trim()
      .min(1, { message: t('validation_required') }),
    field_type: z.enum(SERVICE_CUSTOM_FIELD_TYPES),
    option_values: z.string(),
    required: z.boolean(),
  });

export const createCatalogServiceFormSchema = (t: TFunction) =>
  z
    .object({
      service_category_id: z
        .string()
        .min(1, { message: t('validation_required') }),
      name: z
        .string()
        .trim()
        .min(1, { message: t('validation_required') })
        .max(SERVICE_NAME_MAX_LENGTH, {
          message: t('validation_max_length', {
            count: SERVICE_NAME_MAX_LENGTH,
          }),
        }),
      description: z.string().optional(),
      receipt_name: z
        .string()
        .trim()
        .max(SERVICE_NAME_MAX_LENGTH, {
          message: t('validation_max_length', {
            count: SERVICE_NAME_MAX_LENGTH,
          }),
        })
        .optional(),
      business_direction_id: z
        .string()
        .min(1, { message: t('validation_required') }),
      duration_minutes: z
        .string()
        .refine((value) => value.trim() === '' || Number(value) > 0, {
          message: t('validation_invalid'),
        }),
      price: z
        .string()
        .refine(isNonNegativeNumber, { message: t('validation_invalid') }),
      cost_price: z
        .string()
        .refine((value) => value.trim() === '' || isNonNegativeNumber(value), {
          message: t('validation_invalid'),
        }),
      specialist_payout_tmp: z
        .string()
        .refine((value) => value.trim() === '' || isNonNegativeNumber(value), {
          message: t('validation_invalid'),
        }),
      reason: z.string().optional(),
      location_ids: z
        .array(z.string())
        .min(1, { message: t('validation_required') }),
      requires_specialist: z.boolean(),
      booking_type: z.enum(BOOKING_TYPES),
      color: z.string(),
      notes: z.string().optional(),
      photo: z.union([z.instanceof(File), z.string()]).nullable(),
      service_custom_fields: z.array(createCustomFieldRowSchema(t)),
      service_selling_permissions: z.array(createSubjectRowSchema(t)),
      specialist_ids: z.array(z.string()),
    })
    .superRefine((values, ctx) => {
      const isSlotBooked =
        values.booking_type === BOOKING_TYPE_PERSONAL ||
        values.booking_type === BOOKING_TYPE_GROUP;

      if (isSlotBooked) {
        const duration = Number(values.duration_minutes);
        const isWholeUnits =
          values.duration_minutes.trim() !== '' &&
          duration > 0 &&
          duration % BOOKING_DURATION_UNIT_MINUTES === 0;

        if (!isWholeUnits) {
          ctx.addIssue({
            code: 'custom',
            path: ['duration_minutes'],
            message: t('validation_duration_units', {
              count: BOOKING_DURATION_UNIT_MINUTES,
            }),
          });
        }
      }

      values.service_custom_fields.forEach((field, index) => {
        if (
          field.field_type === CUSTOM_FIELD_TYPE_DROPDOWN &&
          field.option_values.trim() === ''
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['service_custom_fields', index, 'option_values'],
            message: t('validation_required'),
          });
        }
      });
    });

export type CatalogServiceSubjectRow = z.infer<
  ReturnType<typeof createSubjectRowSchema>
>;

export type CatalogServiceCustomFieldRow = z.infer<
  ReturnType<typeof createCustomFieldRowSchema>
>;

export type CatalogServiceFormValues = z.infer<
  ReturnType<typeof createCatalogServiceFormSchema>
>;

export const CATALOG_SERVICE_TAB_GENERAL = 'general' as const;
export const CATALOG_SERVICE_TAB_BOOKING = 'booking' as const;

export type CatalogServiceTab =
  typeof CATALOG_SERVICE_TAB_GENERAL | typeof CATALOG_SERVICE_TAB_BOOKING;

export const CATALOG_SERVICE_FIELD_TABS: Record<
  keyof CatalogServiceFormValues,
  CatalogServiceTab
> = {
  service_category_id: CATALOG_SERVICE_TAB_GENERAL,
  name: CATALOG_SERVICE_TAB_GENERAL,
  receipt_name: CATALOG_SERVICE_TAB_GENERAL,
  business_direction_id: CATALOG_SERVICE_TAB_GENERAL,
  price: CATALOG_SERVICE_TAB_GENERAL,
  reason: CATALOG_SERVICE_TAB_GENERAL,
  cost_price: CATALOG_SERVICE_TAB_GENERAL,
  specialist_payout_tmp: CATALOG_SERVICE_TAB_GENERAL,
  location_ids: CATALOG_SERVICE_TAB_GENERAL,
  notes: CATALOG_SERVICE_TAB_GENERAL,
  requires_specialist: CATALOG_SERVICE_TAB_GENERAL,
  service_selling_permissions: CATALOG_SERVICE_TAB_GENERAL,
  specialist_ids: CATALOG_SERVICE_TAB_GENERAL,
  booking_type: CATALOG_SERVICE_TAB_BOOKING,
  duration_minutes: CATALOG_SERVICE_TAB_BOOKING,
  description: CATALOG_SERVICE_TAB_BOOKING,
  color: CATALOG_SERVICE_TAB_BOOKING,
  photo: CATALOG_SERVICE_TAB_BOOKING,
  service_custom_fields: CATALOG_SERVICE_TAB_BOOKING,
};
